import { forwardRef } from 'react'

import { Button, type IconProps, Text } from '@grammarly/design-system'

import { ActionButtonIcon } from './ActionButtonIcon'

import styles from './ActionButton.module.scss'

export interface ActionButtonProps {
  /**
   * The icon to render.
   */
  readonly iconStart?: React.ReactNode

  /**
   * The optional bold text to render above the `text`.
   */
  readonly title?: string

  /**
   * The text to render.
   */
  readonly text: string

  /**
   * The click handler.
   */
  readonly onClick?: () => void

  /**
   * Optional className to apply to the button.
   */
  readonly className?: string

  /**
   * Whether to allow the button to grow its width - default GDS button is `width: fit-content`
   */
  readonly fullWidth?: boolean
}

/**
 * Generic action button component that renders an icon, title, and text.
 * This is the base UI component used by both ContextualOptionButton and NextActionButton.
 */
export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  ({ iconStart, title, text, onClick, className, fullWidth }, ref) => {
    const icon = iconStart
      ? ((() => <ActionButtonIcon icon={iconStart} />) as unknown as IconProps['icon'])
      : undefined

    return (
      <Button
        ref={ref}
        className={`${styles.button} ${fullWidth ? styles.buttonFullWidth : ''} ${className || ''}`}
        variant="secondary"
        size="large"
        iconStart={icon}
        onClick={onClick}
      >
        {title ? (
          <div>
            <Text as="p" variant="text-small" weight="bold">
              {title}
            </Text>
            <Text as="p" variant="text-small">
              {text}
            </Text>
          </div>
        ) : (
          <Text as="span" variant="text-small">
            {text}
          </Text>
        )}
      </Button>
    )
  },
)
